// Row model shared by the classic grid (TimesheetPreview) and the VP-style grid
// (TimesheetGrid, ADR-0049). Both render the same rows; only the layout differs.

import type { TimesheetRow } from '../api/client';
import { targetKey, type BillingTarget, type DraftResponse } from '../api/draft';
import type { ConfidenceState } from './ui/ConfidenceBar.vue';
import { confidenceBarState, isUncertain } from '../theme/confidence';

export interface PreviewRow {
  // Stable key: `wbs1|wbs2|wbs3|category` for draft rows; `vp:<seq>` for VP rows.
  // VP rows key on Seq (VP's true row identity) so duplicate WBS tuples — two rows
  // for the same project/phase — stay distinct instead of collapsing into one
  // summed row. A draft row's category is part of its identity for the same
  // reason: a time category and a project can share a code, and merging them
  // sums two different save targets into one row the grid cannot resolve back.
  rowKey: string;
  wbs1: string;
  wbs1Name: string;
  wbs2: string;
  wbs2Name: string;
  wbs3: string;
  wbs3Name: string;
  // The classic grid's third line under the project name.
  client?: string;
  // VP's Firm Name: the project's client. Blank on time categories.
  firm: string;
  days: Record<number, number | undefined>;
  // VP rows only: the overtime share of `days`, and the comment VP holds per day.
  overtime: Record<number, number>;
  comments: Record<number, string>;
  uncertain: Record<number, boolean>;
  // Row-level target confidence rail state; 'confirmed' when the user has picked
  // the target. Undefined on VP (existing) rows — no rail rendered.
  confBar?: ConfidenceState;
  isDraft: boolean;
  isPhased: boolean;
  // Ghost preview (Triage resolver): the cell the staged pick would land in.
  // isGhost marks a synthetic row that exists only for the preview.
  isGhost?: boolean;
  ghostDay?: number;
  ghostHours?: number;
  // isPhased && !wbs2 — engine placed this row on a phased project with no leaf.
  // The row is kept visible but flagged; Verify & Save blocks until resolved.
  needsPhase: boolean;
  // isPhased && wbs2 set && the chosen phase has tasks but no task is selected.
  // Backend normally auto-defaults to the first task, so this is only set on
  // edge rows where auto-default couldn't resolve (e.g. all tasks inactive).
  needsTask: boolean;
  // Default order within the row's group: VP's Seq for VP rows, arrival order in
  // the draft response for draft rows.
  order: number;
}

export interface PreviewRowInput {
  vpRows: TimesheetRow[];
  draftRows?: DraftResponse;
  ghostCell?: { target: BillingTarget; dayIndex: number; hours: number } | null;
}

export function buildPreviewRows({ vpRows, draftRows, ghostCell }: PreviewRowInput): PreviewRow[] {
  const map = new Map<string, PreviewRow>();

  // A draft row's firm is the one VP shows for the same project when the sheet
  // has it: the draft's own source is the project's billing client.
  const vpFirms = new Map<string, string>();
  for (const r of vpRows) {
    if (r.clientName && !vpFirms.has(r.wbs1)) vpFirms.set(r.wbs1, r.clientName);
  }
  const projectClients = new Map<string, string>();
  for (const pc of draftRows?.projectClients ?? []) projectClients.set(pc.wbs1.toUpperCase(), pc.clientName);
  const firmFor = (wbs1: string): string => vpFirms.get(wbs1) ?? projectClients.get(wbs1.toUpperCase()) ?? '';

  // VP (existing) rows render 1:1, keyed by Seq (VP's true row identity, array
  // index as a fallback for fixtures that omit it). Duplicate WBS tuples — two
  // VP rows for the same project/phase, distinct by Seq — therefore stay as two
  // rows rather than collapsing into one summed row.
  vpRows.forEach((vpRow, i) => {
    const key = `vp:${vpRow.seq ?? i}`;
    const row: PreviewRow = {
      rowKey: key,
      wbs1: vpRow.wbs1,
      wbs1Name: vpRow.wbs1Name || '(unnamed)',
      wbs2: vpRow.wbs2,
      wbs2Name: vpRow.wbs2Name,
      wbs3: vpRow.wbs3,
      wbs3Name: vpRow.wbs3Name,
      firm: vpRow.clientName ?? '',
      days: {},
      overtime: {},
      comments: {},
      uncertain: {},
      isDraft: false,
      isPhased: !!vpRow.wbs2,
      needsPhase: false,
      needsTask: false,
      order: vpRow.seq ?? i,
    };
    for (const cell of vpRow.days) {
      if (cell.total > 0) row.days[cell.index - 1] = cell.total;
      if (cell.overtime > 0) row.overtime[cell.index - 1] = cell.overtime;
      if (cell.comment) row.comments[cell.index - 1] = cell.comment;
    }
    map.set(key, row);
  });

  if (draftRows) {
    draftRows.rows.forEach((draftRow, i) => {
      const { wbs1, wbs2, wbs3 } = draftRow.target;
      const key = targetKey(draftRow.target);
      const needsPhase = !!(draftRow.isPhased && !wbs2);
      const needsTask = !!(draftRow.isPhased && wbs2 && !wbs3 && draftRow.needsTask);
      if (!map.has(key)) {
        map.set(key, {
          rowKey: key,
          wbs1,
          wbs1Name: draftRow.target.wbs1Name,
          wbs2,
          wbs2Name: draftRow.target.wbs2Name,
          wbs3,
          wbs3Name: draftRow.target.wbs3Name,
          client: draftRow.target.wbs2Name || undefined,
          firm: draftRow.target.category ? '' : firmFor(wbs1),
          days: {},
          overtime: {},
          comments: {},
          uncertain: {},
          // Confirmed picks show the blue 'confirmed' rail; otherwise the row's
          // target confidence drives green/amber/red.
          confBar: draftRow.confirmed ? 'confirmed' : confidenceBarState(draftRow.confidence.target),
          isDraft: true,
          isPhased: !!(draftRow.isPhased),
          needsPhase,
          needsTask,
          order: i,
        });
      }
      const row = map.get(key)!;
      for (const dh of draftRow.days) {
        // Work already recorded is not outstanding, so it leaves the draft
        // altogether: it is shown under In Vantagepoint as the hours it became, and the
        // event behind it under Published or Duplicate in the triage drawer
        // (#6487). Keeping it here made the same work read twice and doubled
        // the day against Vantagepoint.
        if (dh.written) continue;
        if (dh.hours > 0) {
          row.days[dh.dayIndex] = (row.days[dh.dayIndex] ?? 0) + dh.hours;
        }
        // The amber corner flag clears once the row is confirmed (its rail is
        // already blue) — a confirmed cell no longer prompts "review this".
        if (!draftRow.confirmed && isUncertain(draftRow.confidence)) {
          row.uncertain[dh.dayIndex] = true;
        }
      }
    });
  }

  if (ghostCell) {
    const { target, dayIndex, hours } = ghostCell;
    const key = targetKey(target);
    const hit = map.get(key);
    if (hit?.isDraft) {
      hit.ghostDay = dayIndex;
      hit.ghostHours = hours;
    } else {
      map.set(key, {
        rowKey: `ghost:${key}`,
        wbs1: target.wbs1,
        wbs1Name: target.wbs1Name,
        wbs2: target.wbs2,
        wbs2Name: target.wbs2Name,
        wbs3: target.wbs3,
        wbs3Name: target.wbs3Name,
        firm: target.category ? '' : firmFor(target.wbs1),
        days: {},
        overtime: {},
        comments: {},
        uncertain: {},
        isDraft: false,
        isGhost: true,
        ghostDay: dayIndex,
        ghostHours: hours,
        isPhased: !!target.wbs2,
        needsPhase: false,
        needsTask: false,
        order: Number.MAX_SAFE_INTEGER,
      });
    }
  }

  const existing = Array.from(map.values()).filter(r => !r.isDraft && !r.isGhost);
  // A draft row whose every cell was already recorded has nothing outstanding
  // left to show, so it leaves the grid rather than sitting at zero. A ghost
  // row carries a staged pick and has no cells yet, so it always stays.
  const draftList = Array.from(map.values())
    .filter(r => r.isDraft || r.isGhost)
    .filter(r => r.isGhost || Object.keys(r.days).length > 0);
  return [...existing, ...draftList];
}

export function rowTotal(row: PreviewRow): number {
  return Object.values(row.days).reduce<number>((sum, v) => sum + (v ?? 0), 0);
}

export interface PeriodDay {
  // ISO yyyy-mm-dd
  iso: string;
  // Sun..Sat
  dow: string;
  // 1-based month and day, for VP's "9/16" header form.
  month: number;
  day: number;
  isNonWorking: boolean;
}

const DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function buildPeriodDays(periodStart: string, dayCount: number, nonWorkingDays: number[]): PeriodDay[] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodStart);
  if (!m) {
    return Array.from({ length: dayCount }, (_, i) => ({
      iso: '', dow: 'Mon', month: 0, day: i + 1, isNonWorking: false,
    }));
  }
  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const nonWorking = new Set(nonWorkingDays);
  return Array.from({ length: dayCount }, (_, i) => {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + i);
    return {
      iso: d.toISOString().slice(0, 10),
      dow: DOWS[d.getUTCDay()],
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      isNonWorking: nonWorking.has(d.getUTCDay()),
    };
  });
}

// rowKeys with at least one contributing event whose note is being
// auto-regenerated (Item 5, ux-batch-2026-07).
export function regeneratingRowKeys(draftRows: DraftResponse | undefined, ids: Set<string> | undefined): Set<string> {
  const set = new Set<string>();
  if (!ids || ids.size === 0 || !draftRows) return set;
  for (const row of draftRows.rows) {
    if (row.contributingEvents.some(ce => ids.has(ce.eventId))) set.add(targetKey(row.target));
  }
  return set;
}

export interface HighlightCell {
  wbs1: string;
  dayIndexes: number[];
  scope?: 'draft' | 'vp';
}

// Item 5: selecting a draft row must not bleed the highlight onto the VP rows
// sharing its project. But a published card has no draft row left — its work is
// only in VP — so nothing would highlight at all. Highlight the VP row only when
// no draft row owns that project (#6487).
export function isRowHighlighted(
  row: PreviewRow,
  highlightWbs: string | undefined,
  highlightScope: 'draft' | 'vp' | undefined,
  cell: HighlightCell | null | undefined,
): boolean {
  if (highlightWbs !== row.wbs1) return false;
  // Item 5 still holds for a draft-scoped selection: an Attributed card
  // highlights its draft row and must not bleed onto the VP rows sharing the
  // project. A VP-scoped selection is the mirror image (#6487).
  const inScope = highlightScope === 'vp' ? !row.isDraft : row.isDraft;
  if (!inScope) return false;

  // Vantagepoint keys rows on Seq, so one project + phase can hold several rows
  // in the same period (Bug 5956), and matching on the project alone lights up
  // every one of them. The card points at a single day, so when a cell is in
  // hand only the row actually carrying that day is the row it means.
  if (!cell || cell.wbs1 !== row.wbs1) return true;
  return cell.dayIndexes.some(di => row.days[di] !== undefined);
}

// The clicked card's own cell, in whichever block holds its hours. VP numbers
// days from 1 and the draft from 0; highlightCell speaks the draft's language.
export function isCellHighlighted(row: PreviewRow, dayIndex: number, cell: HighlightCell | null | undefined): boolean {
  if (!cell || cell.wbs1 !== row.wbs1 || !cell.dayIndexes.includes(dayIndex)) return false;
  if ((cell.scope ?? 'draft') === 'vp' ? row.isDraft : !row.isDraft) return false;
  // Same reason as the row: several VP rows can share the tuple, and tinting the
  // empty cell in the ones that do not hold the hours points at nothing.
  return row.days[dayIndex] !== undefined || row.ghostDay === dayIndex;
}

// A Task cell only opens the picker when the phase actually has tasks (one is
// chosen, or one still needs choosing). A phase with no tasks is a leaf — its
// Task cell is inert and shows a dash, exactly like a flat project's Phase cell.
export function taskCellActionable(row: PreviewRow): boolean {
  return row.isDraft && row.isPhased && (row.needsTask || !!row.wbs3);
}

// Ghost cell shows the staged pick's hours added to whatever the cell already
// holds — the value the cell would read after confirm. Null → no ghost here.
export function ghostCellText(row: PreviewRow, dayIndex: number): string | null {
  if (row.ghostDay !== dayIndex || row.ghostHours === undefined) return null;
  return ((row.days[dayIndex] ?? 0) + row.ghostHours).toFixed(2);
}

// Every draft cell opens the cell-detail modal — a filled one to edit its
// hours, an empty one to put hours on that day. VP (existing) rows are inert.
export function cellAriaLabel(row: PreviewRow, dayIndex: number, dow: string): string | undefined {
  if (!row.isDraft) return undefined;
  if (!row.days[dayIndex]) return `${row.wbs1} on ${dow} — add hours`;
  return `${row.wbs1} on ${dow} — open detail`;
}
